import React, { useState, useMemo } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import { TableGrid } from '../../../../presentation';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import {
  useReservesQuery,
  getPaginatedNodes,
  PaymentStatus,
} from '@converge/graphql';
import { useParams } from 'react-router-dom';
import './payment-history-tab.scss';

interface PaymentHistoryTabProps {
  formatDollarAmount: (amount: number) => string;
  claimUuid?: string;
}

interface PaymentHistoryData {
  id: string;
  reserveType: string;
  vendor: string;
  payee: string;
  amount: number;
  paymentDate: string;
  invoiceReference: string;
  invoiceDate: string;
  status: string;
  type: string;
}

export const PaymentHistoryTab: React.FC<PaymentHistoryTabProps> = ({
  formatDollarAmount,
  claimUuid,
}) => {
  const params = useParams<{ claimUuid: string }>();
  const claimUuidFromParams = params.claimUuid || claimUuid;

  // Fetch reserves data to get payments
  const { data: reservesData, loading: reservesLoading } = useReservesQuery({
    claimUuid: claimUuidFromParams,
  });
  // Process payments data from reserves
  const paymentHistoryData = useMemo(() => {
    if (!reservesData?.reserves?.edges) return [];

    const allPayments: PaymentHistoryData[] = [];

    reservesData.reserves.edges.forEach((reserveEdge) => {
      const reserve = reserveEdge.node;
      const reserveType = reserve?.reserveType?.name || 'Unknown';

      if (reserve?.payments?.edges) {
        const payments = getPaginatedNodes(reserve.payments);

        payments.forEach((payment) => {
          if (payment.status !== PaymentStatus.Open) {
            allPayments.push({
              id: payment.uuid || '',
              reserveType: reserveType,
              vendor:
                payment.vendor?.node?.aliases.edges[0]?.node?.name || 'N/A',
              payee:
                payment.vendor?.node?.aliases.edges[0]?.node?.name ||
                payment.insured?.node?.aliases.edges[0]?.node?.name ||
                'N/A',
              amount:
                payment.reserveAmount && payment.reserveAmount !== 0
                  ? payment.reserveAmount
                  : payment.retentionAmount ?? 0,
              paymentDate: payment.paidAt
                ? new Date(payment.paidAt).toLocaleDateString()
                : 'N/A',
              invoiceReference: payment.invoiceNumber || 'N/A',
              invoiceDate: payment.paidAt
                ? new Date(payment.paidAt).toLocaleDateString()
                : 'N/A',
              status: payment.status || 'N/A',
              type: payment.type || 'N/A',
            });
          }
        });
      }
    });

    return allPayments;
  }, [reservesData]);

  // Table pagination and sorting state
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentSortModel, setCurrentSortModel] = useState<GridSortModel>([
    { field: 'paymentDate', sort: 'desc' },
  ]);

  // Table columns definition
  const columns: GridColDef[] = [
    {
      field: 'reserveType',
      headerName: 'RESERVE TYPE',
      flex: 1,
      renderCell: (params) => (
        <Typography className="table-cell">{params.value}</Typography>
      ),
    },
    {
      field: 'vendor',
      headerName: 'VENDOR',
      flex: 1.2,
      renderCell: (params) => (
        <Typography className="table-cell">{params.value}</Typography>
      ),
    },
    {
      field: 'payee',
      headerName: 'PAYEE',
      flex: 1.2,
      renderCell: (params) => (
        <Typography className="table-cell">{params.value}</Typography>
      ),
    },
    {
      field: 'invoiceReference',
      headerName: 'INVOICE REF',
      flex: 1,
      renderCell: (params) => (
        <Typography className="table-cell">{params.value}</Typography>
      ),
    },
    {
      field: 'amount',
      headerName: 'AMOUNT',
      flex: 1,
      renderCell: (params) => (
        <Typography className="table-cell">
          {formatDollarAmount(params.value)}
        </Typography>
      ),
    },
    {
      field: 'invoiceDate',
      headerName: 'INVOICE DATE',
      flex: 1,
      renderCell: (params) => (
        <Typography className="table-cell">{params.value}</Typography>
      ),
    },
    {
      field: 'paymentDate',
      headerName: 'PAYMENT DATE',
      flex: 1,
      renderCell: (params) => (
        <Typography className="table-cell">{params.value}</Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'STATUS',
      flex: 1,
      renderCell: (params) => (
        <Typography className="table-cell">{params.value}</Typography>
      ),
    },
  ];

  // Handle sort model change
  const handleSortModelChange = (sortModel: GridSortModel) => {
    setCurrentSortModel(sortModel);
  };

  // Calculate totals from actual data
  const totalPaidOut = useMemo(() => {
    return paymentHistoryData
      .filter((payment) => payment.status === PaymentStatus.Paid)
      .reduce((sum, payment) => sum + payment.amount, 0);
  }, [paymentHistoryData]);

  const totalReserveAmount = useMemo(() => {
    if (!reservesData?.reserves?.edges) return 0;
    return reservesData.reserves.edges.reduce((sum, edge) => {
      return (
        sum +
        (edge.node?.remainingReserve || 0) +
        (edge.node?.paymentTotal || 0)
      );
    }, 0);
  }, [reservesData]);

  if (reservesLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 4,
          margin: 'auto',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="payment-history-container" sx={{ position: 'relative' }}>
      {/* Financial Summary Cards - Fixed Position under Tab Navigation */}
      <Box
        className="summary-cards-container"
        sx={{
          position: 'fixed',
          top: '365px', // Positioned below the tabs (290px + padding)
          zIndex: 1,
          width: '83.5%',
          backgroundColor: 'white',
          paddingTop: '10px',
          paddingBottom: '10px',
          marginLeft: '1.5rem',
        }}
      >
        <Grid container spacing={2} className="card-container">
          {/* First Card - Reserve Information */}
          <Grid item xs={12} md={6}>
            <Paper className="summary-card">
              <Box className="reserve-card-content">
                <Box className="field-container">
                  <Typography className="field-label">
                    TOTAL RESERVES
                  </Typography>
                  <Typography className="field-value">
                    {formatDollarAmount(totalReserveAmount)}
                  </Typography>
                </Box>

                <Box className="field-container">
                  <Typography className="field-label">
                    TOTAL PAID OUT
                  </Typography>
                  <Typography className="field-value">
                    {formatDollarAmount(totalPaidOut)}
                  </Typography>
                </Box>

                <Box className="field-container">
                  <Typography className="field-label">
                    REMAINING RESERVE
                  </Typography>
                  <Typography className="field-value">
                    {formatDollarAmount(
                      Math.max(0, totalReserveAmount - totalPaidOut)
                    )}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Second Card - Recovery Information */}
          <Grid item xs={12} md={3}>
            <Paper className="summary-card">
              <Box className="card-content">
                <Typography className="field-label">
                  RECOVERY RECEIVED
                </Typography>
                <Typography className="field-value">
                  {formatDollarAmount(0)}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Third Card - Payment Information */}
          <Grid item xs={12} md={3}>
            <Paper className="summary-card">
              <Box className="card-content">
                <Typography className="field-label">PAYMENT COUNT</Typography>
                <Typography className="field-value">
                  {paymentHistoryData.length}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Payment History Table - Fixed position below summary cards */}
      <Box
        className="payment-history-table"
        sx={{
          position: 'fixed',
          top: '465px', // Position below summary cards (350px + ~90px height of cards)
          zIndex: 1,
          width: '83.5%',
          height: 'calc(100vh - 600px)', // Calculate remaining viewport height
          marginLeft: '1.5rem',
        }}
      >
        <TableGrid
          rows={paymentHistoryData}
          columns={columns}
          currentPage={currentPage}
          pageSize={pageSize}
          handleChangePageNumber={(pageNumber: number) =>
            setCurrentPage(pageNumber)
          }
          handlePageSizeChange={(newPageSize: number) =>
            setPageSize(newPageSize)
          }
          totalResults={paymentHistoryData.length}
          sortModel={currentSortModel}
          handleSortModelChange={handleSortModelChange}
          rowHeight={48}
          isPaymentTable={true}
          message={
            'There are no payments currently associated with this claim.'
          }
        />
      </Box>
    </Box>
  );
};
